import React, { useState, useEffect, useCallback } from 'react';
import { apiClient, getCurrentToken } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
} from '../legacy-ball/table-management/designTokens';

/* ─── Types ─── */
interface BetaTester {
  id: string;
  ticketNumber: string;
  email: string;
  lastName: string;
  fullName: string;
  orderRef: string | null;
  optInSource: 'LinkAudit' | 'Manual';
  active: boolean;
  whitelistedAt: string;
  lastLoginAt: string | null;
  guestRecord: { id: string; fullName: string; ticketNumber: string | null; chapterAffiliation: string | null };
}

interface Candidate {
  guestRecordId: string;
  fullName: string;
  ticketNumber: string | null;
  chapter: string | null;
  email: string | null;
  orderRef: string | null;
}

interface AuditEntry {
  id: string;
  identifier: string;
  outcome: string;
  failureReason: string | null;
  ipAddress: string;
  city: string | null;
  country: string | null;
  timestamp: string;
}

interface Stats {
  total: number;
  active: number;
  linkAudit: number;
  manual: number;
  recentLogins24h: number;
}

interface LookupResult {
  found: boolean;
  fullName?: string;
  username?: string;
  passwordHint?: string;
  ticketNumberPreview?: string;
  message?: string;
}

type PanelView = 'whitelist' | 'add' | 'lookup' | 'audit';

/* ─── Styles ─── */
const s: Record<string, React.CSSProperties> = {
  container: { maxWidth: '900px' },
  statsRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '1rem' },
  statCard: { background: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '10px', padding: '12px 16px', flex: '1 1 140px', minWidth: '120px', textAlign: 'center' },
  statValue: { fontSize: '1.4rem', fontWeight: 700, color: PRIMARY },
  statLabel: { fontSize: '0.7rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' },
  tabRow: { display: 'flex', gap: '4px', marginBottom: '1rem', borderBottom: `2px solid ${BORDER_LIGHT}`, paddingBottom: '0' },
  tab: { padding: '8px 16px', fontSize: '0.82rem', fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer', color: TEXT_MUTED, borderBottom: '2px solid transparent', marginBottom: '-2px', transition: 'all 0.15s' },
  tabActive: { padding: '8px 16px', fontSize: '0.82rem', fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer', color: PRIMARY, borderBottom: `2px solid ${PRIMARY}`, marginBottom: '-2px' },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.78rem' },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: `2px solid ${PRIMARY}`, fontWeight: 600, color: TEXT_MUTED, fontSize: '0.72rem', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  td: { padding: '8px 10px', borderBottom: `1px solid ${BORDER_LIGHT}`, color: '#333', verticalAlign: 'top' as const },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 600 },
  badgeActive: { backgroundColor: '#d1fae5', color: '#065f46' },
  badgeInactive: { backgroundColor: '#fee2e2', color: '#991b1b' },
  badgeLinkAudit: { backgroundColor: '#dbeafe', color: '#1e40af' },
  badgeManual: { backgroundColor: '#fef3c7', color: '#92400e' },
  btn: { padding: '6px 14px', borderRadius: '6px', border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' },
  btnPrimary: { backgroundColor: PRIMARY, color: '#fff' },
  btnDanger: { backgroundColor: '#dc2626', color: '#fff' },
  btnSecondary: { backgroundColor: '#e5e7eb', color: '#333' },
  input: { width: '100%', padding: '8px 12px', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box' as const },
  section: { background: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '10px', padding: '1rem', marginBottom: '1rem' },
  sectionTitle: { fontSize: '0.85rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.75rem' },
  lookupCard: { background: BG_SURFACE, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '10px', padding: '1.25rem', marginTop: '1rem', textAlign: 'center' as const },
  credentialBox: { background: '#f0fdf4', border: `2px solid ${PRIMARY}`, borderRadius: '10px', padding: '1.25rem', marginTop: '1rem' },
  emptyState: { textAlign: 'center' as const, padding: '2rem', color: TEXT_MUTED, fontSize: '0.85rem' },
};

/* ─── Component ─── */
export function BetaTestingPanel() {
  const [view, setView] = useState<PanelView>('whitelist');
  const [stats, setStats] = useState<Stats | null>(null);
  const [whitelist, setWhitelist] = useState<BetaTester[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [toast, setToast] = useState('');

  // Lookup state
  const [lookupMode, setLookupMode] = useState<'email' | 'order'>('email');
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupOrder, setLookupOrder] = useState('');
  const [lookupLastName, setLookupLastName] = useState('');
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // ── Data Loading ──
  const loadStats = useCallback(async () => {
    try {
      const data = await apiClient<Stats>('/beta-testing/stats');
      setStats(data);
    } catch {}
  }, []);

  const loadWhitelist = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient<BetaTester[]>('/beta-testing/whitelist');
      setWhitelist(data);
    } catch {}
    setLoading(false);
  }, []);

  const loadCandidates = useCallback(async (search?: string) => {
    setLoading(true);
    try {
      const q = search ? `?search=${encodeURIComponent(search)}` : '';
      const data = await apiClient<Candidate[]>(`/beta-testing/candidates${q}`);
      setCandidates(data);
    } catch {}
    setLoading(false);
  }, []);

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient<AuditEntry[]>('/beta-testing/audit?limit=50');
      setAudit(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    if (view === 'whitelist') loadWhitelist();
    else if (view === 'add') loadCandidates();
    else if (view === 'audit') loadAudit();
  }, [view, loadWhitelist, loadCandidates, loadAudit]);

  // ── Actions ──
  const handleAddTester = async (guestRecordId: string) => {
    try {
      await apiClient('/beta-testing/whitelist', { method: 'POST', body: { guestRecordId } });
      setToast('Added to beta whitelist');
      loadCandidates(candidateSearch || undefined);
      loadStats();
    } catch (e: any) {
      setToast(e?.message || 'Failed to add');
    }
    setTimeout(() => setToast(''), 3000);
  };

  const handleRemove = async (id: string) => {
    if (!confirm('Remove this tester from the whitelist?')) return;
    try {
      await apiClient(`/beta-testing/whitelist/${id}`, { method: 'DELETE' });
      setWhitelist(prev => prev.filter(t => t.id !== id));
      loadStats();
      setToast('Removed');
    } catch {}
    setTimeout(() => setToast(''), 3000);
  };

  const handleDeactivate = async (id: string) => {
    try {
      await apiClient(`/beta-testing/whitelist/${id}/deactivate`, { method: 'PATCH' });
      setWhitelist(prev => prev.map(t => t.id === id ? { ...t, active: false } : t));
      loadStats();
      setToast('Deactivated');
    } catch {}
    setTimeout(() => setToast(''), 3000);
  };

  const handleLookup = async () => {
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const body: any = { lastName: lookupLastName };
      if (lookupMode === 'email') body.email = lookupEmail;
      else body.orderId = lookupOrder;
      const result = await apiClient<LookupResult>('/beta-testing/lookup-credentials', { method: 'POST', body });
      setLookupResult(result);
    } catch (e: any) {
      setLookupResult({ found: false, message: e?.message || 'Lookup failed' });
    }
    setLookupLoading(false);
  };

  // ── Candidate search debounce ──
  useEffect(() => {
    if (view !== 'add') return;
    const timer = setTimeout(() => loadCandidates(candidateSearch || undefined), 400);
    return () => clearTimeout(timer);
  }, [candidateSearch, view, loadCandidates]);

  function formatTime(iso: string) {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // ── Render ──
  return (
    <div style={s.container}>
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: '16px', right: '16px', background: '#065f46', color: '#fff', padding: '10px 18px', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          {toast}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div style={s.statsRow}>
          <div style={s.statCard}><div style={s.statValue}>{stats.total}</div><div style={s.statLabel}>Total Testers</div></div>
          <div style={s.statCard}><div style={s.statValue}>{stats.active}</div><div style={s.statLabel}>Active</div></div>
          <div style={s.statCard}><div style={s.statValue}>{stats.linkAudit}</div><div style={s.statLabel}>Via Link Audit</div></div>
          <div style={s.statCard}><div style={s.statValue}>{stats.manual}</div><div style={s.statLabel}>Manual</div></div>
          <div style={s.statCard}><div style={{ ...s.statValue, color: stats.recentLogins24h > 0 ? '#059669' : TEXT_MUTED }}>{stats.recentLogins24h}</div><div style={s.statLabel}>Logins (24h)</div></div>
        </div>
      )}

      {/* Report Link */}
      <div style={{ marginBottom: '1rem' }}>
        <button
          onClick={() => {
            const token = getCurrentToken();
            window.open(`/beta-testing-login-report.html#token=${token}`, '_blank');
          }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '0.82rem', fontWeight: 600, color: '#fff', backgroundColor: PRIMARY, borderRadius: '6px', textDecoration: 'none', cursor: 'pointer', border: 'none' }}
        >
          📊 Login Infographic Report
        </button>
      </div>

      {/* Sub-tabs */}
      <nav style={s.tabRow} aria-label="Beta testing views">
        {([
          { key: 'whitelist', label: '📋 Whitelist' },
          { key: 'add', label: '➕ Add Testers' },
          { key: 'lookup', label: '🔍 Credential Lookup' },
          { key: 'audit', label: '📊 Login Activity' },
        ] as { key: PanelView; label: string }[]).map(tab => (
          <button
            key={tab.key}
            style={view === tab.key ? s.tabActive : s.tab}
            onClick={() => setView(tab.key)}
            aria-current={view === tab.key ? 'page' : undefined}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* ═══ WHITELIST VIEW ═══ */}
      {view === 'whitelist' && (
        <div style={s.section}>
          {/* Beta Welcome Message Editor */}
          <BetaMessageEditor />
          <div style={{ borderTop: `1px solid ${BORDER_LIGHT}`, marginTop: '1rem', paddingTop: '1rem' }}>
          <div style={s.sectionTitle}>Beta Testing Whitelist ({whitelist.length})</div>
          {loading && <div style={s.emptyState}>Loading...</div>}
          {!loading && whitelist.length === 0 && (
            <div style={s.emptyState}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🧪</div>
              No testers whitelisted yet. Use "Add Testers" to get started.
            </div>
          )}
          {!loading && whitelist.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Name</th>
                    <th style={s.th}>Email</th>
                    <th style={s.th}>Ticket</th>
                    <th style={s.th}>Order</th>
                    <th style={s.th}>Source</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Last Login</th>
                    <th style={s.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {whitelist.map(t => (
                    <tr key={t.id}>
                      <td style={s.td}><span style={{ fontWeight: 600 }}>{t.fullName}</span></td>
                      <td style={s.td}>{t.email}</td>
                      <td style={s.td}><code style={{ fontSize: '0.75rem' }}>{t.ticketNumber}</code></td>
                      <td style={s.td}>{t.orderRef || '—'}</td>
                      <td style={s.td}>
                        <span style={{ ...s.badge, ...(t.optInSource === 'LinkAudit' ? s.badgeLinkAudit : s.badgeManual) }}>
                          {t.optInSource === 'LinkAudit' ? 'Link Audit' : 'Manual'}
                        </span>
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.badge, ...(t.active ? s.badgeActive : s.badgeInactive) }}>
                          {t.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={s.td}>{t.lastLoginAt ? formatTime(t.lastLoginAt) : '—'}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {t.active && (
                            <button onClick={() => handleDeactivate(t.id)} style={{ ...s.btn, ...s.btnSecondary, padding: '4px 8px', fontSize: '0.72rem' }} title="Deactivate">⏸️</button>
                          )}
                          <button onClick={() => handleRemove(t.id)} style={{ ...s.btn, ...s.btnDanger, padding: '4px 8px', fontSize: '0.72rem' }} title="Remove" aria-label={`Remove ${t.fullName}`}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>
        </div>
      )}

      {/* ═══ ADD TESTERS VIEW ═══ */}
      {view === 'add' && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Select Guests to Add to Beta Testing</div>
          <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginBottom: '0.75rem' }}>
            Showing eligible guests (with user account + ticket) not yet whitelisted.
          </p>
          <input
            style={s.input}
            placeholder="Search by name or ticket number..."
            value={candidateSearch}
            onChange={e => setCandidateSearch(e.target.value)}
          />
          {loading && <div style={{ ...s.emptyState, padding: '1rem' }}>Loading candidates...</div>}
          {!loading && candidates.length === 0 && (
            <div style={s.emptyState}>No eligible candidates found.</div>
          )}
          {!loading && candidates.length > 0 && (
            <div style={{ overflowX: 'auto', marginTop: '0.75rem' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Name</th>
                    <th style={s.th}>Ticket</th>
                    <th style={s.th}>Chapter</th>
                    <th style={s.th}>Email</th>
                    <th style={s.th}>Order</th>
                    <th style={s.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map(c => (
                    <tr key={c.guestRecordId}>
                      <td style={s.td}><span style={{ fontWeight: 600 }}>{c.fullName}</span></td>
                      <td style={s.td}><code style={{ fontSize: '0.75rem' }}>{c.ticketNumber || '—'}</code></td>
                      <td style={s.td}>{c.chapter || '—'}</td>
                      <td style={s.td}>{c.email || '—'}</td>
                      <td style={s.td}>{c.orderRef || '—'}</td>
                      <td style={s.td}>
                        <button
                          onClick={() => handleAddTester(c.guestRecordId)}
                          style={{ ...s.btn, ...s.btnPrimary, padding: '4px 10px', fontSize: '0.72rem' }}
                          disabled={!c.email || !c.ticketNumber}
                          title={!c.email || !c.ticketNumber ? 'Missing email or ticket' : 'Add to whitelist'}
                        >
                          + Add
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══ CREDENTIAL LOOKUP VIEW ═══ */}
      {view === 'lookup' && (
        <div style={s.section}>
          <div style={s.sectionTitle}>🔍 Find My Login Credentials</div>
          <p style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginBottom: '1rem' }}>
            Beta testers can search for their credentials using email + last name, or order ID + last name.
          </p>

          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
            <button
              onClick={() => setLookupMode('email')}
              style={{ ...s.btn, ...(lookupMode === 'email' ? s.btnPrimary : s.btnSecondary) }}
            >
              Search by Email
            </button>
            <button
              onClick={() => setLookupMode('order')}
              style={{ ...s.btn, ...(lookupMode === 'order' ? s.btnPrimary : s.btnSecondary) }}
            >
              Search by Order ID
            </button>
          </div>

          {/* Fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '400px' }}>
            {lookupMode === 'email' ? (
              <input style={s.input} placeholder="Email address" value={lookupEmail} onChange={e => setLookupEmail(e.target.value)} type="email" />
            ) : (
              <input style={s.input} placeholder="Order reference ID" value={lookupOrder} onChange={e => setLookupOrder(e.target.value)} />
            )}
            <input style={s.input} placeholder="Last name" value={lookupLastName} onChange={e => setLookupLastName(e.target.value)} />
            <button
              onClick={handleLookup}
              disabled={lookupLoading || !lookupLastName.trim() || (lookupMode === 'email' ? !lookupEmail.trim() : !lookupOrder.trim())}
              style={{ ...s.btn, ...s.btnPrimary, opacity: lookupLoading ? 0.5 : 1, alignSelf: 'flex-start' }}
            >
              {lookupLoading ? 'Searching...' : 'Find Credentials'}
            </button>
          </div>

          {/* Result */}
          {lookupResult && (
            lookupResult.found ? (
              <div style={s.credentialBox}>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: PRIMARY, marginBottom: '8px' }}>✅ Credentials Found</div>
                <div style={{ fontSize: '0.88rem', lineHeight: 1.8 }}>
                  <div><strong>Name:</strong> {lookupResult.fullName}</div>
                  <div><strong>Username (email):</strong> {lookupResult.username}</div>
                  <div><strong>Password:</strong> {lookupResult.passwordHint}</div>
                  <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginTop: '8px' }}>
                    Use these credentials on the main login page. Your password is your ticket number.
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ ...s.lookupCard, background: '#fef2f2', border: '1px solid #fca5a5' }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#991b1b', marginBottom: '6px' }}>❌ Not Found</div>
                <div style={{ fontSize: '0.82rem', color: '#7f1d1d' }}>{lookupResult.message}</div>
              </div>
            )
          )}
        </div>
      )}

      {/* ═══ AUDIT VIEW ═══ */}
      {view === 'audit' && (
        <div style={s.section}>
          <div style={s.sectionTitle}>📊 Beta Testing Login Activity</div>
          {loading && <div style={s.emptyState}>Loading audit log...</div>}
          {!loading && audit.length === 0 && (
            <div style={s.emptyState}>No beta login activity yet.</div>
          )}
          {!loading && audit.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Time</th>
                    <th style={s.th}>Email</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Reason</th>
                    <th style={s.th}>Location</th>
                    <th style={s.th}>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map(a => (
                    <tr key={a.id}>
                      <td style={s.td}>{formatTime(a.timestamp)}</td>
                      <td style={s.td}>{a.identifier}</td>
                      <td style={s.td}>
                        <span style={{ ...s.badge, ...(a.outcome === 'success' ? s.badgeActive : s.badgeInactive) }}>
                          {a.outcome === 'success' ? '✓ Success' : '✗ Failed'}
                        </span>
                      </td>
                      <td style={s.td}>{a.failureReason || '—'}</td>
                      <td style={s.td}>{[a.city, a.country].filter(Boolean).join(', ') || '—'}</td>
                      <td style={s.td}><code style={{ fontSize: '0.72rem' }}>{a.ipAddress}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Beta Message Editor Sub-Component ─── */
function BetaMessageEditor() {
  const [message, setMessage] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiClient<{ message: string }>('/beta-testing/message')
      .then(d => { setMessage(d.message); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await apiClient('/beta-testing/message', { method: 'PATCH', body: { message } });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {}
    setSaving(false);
  };

  if (!loaded) return null;

  return (
    <div>
      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '6px' }}>
        Beta Tester Welcome Message
      </div>
      <p style={{ fontSize: '0.78rem', color: TEXT_MUTED, margin: '0 0 8px' }}>
        This message is shown to beta testers when they log in (separate from the general login broadcast).
      </p>
      <textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        placeholder="Welcome to KDU Hub beta testing! Please explore the app and report any issues..."
        rows={3}
        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', fontSize: '0.85rem', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
        <button onClick={handleSave} disabled={saving} style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', backgroundColor: PRIMARY, color: '#fff', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving...' : 'Save Message'}
        </button>
        {saved && <span style={{ fontSize: '0.78rem', color: '#065f46', fontWeight: 600 }}>Saved</span>}
      </div>
    </div>
  );
}
